import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserData } from '../app/auth/authSlice';
import userDataService from '../services/userDataService';

/**
 * Custom hook to handle automatic user data refresh
 * Refreshes profile and settings data every 10 minutes
 */

export const useUserDataRefresh = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, lastDataRefresh } = useSelector(state => state.auth);
  const intervalRef = useRef(null);

  const refreshUserData = async () => {
    try {
      const userData = await userDataService.fetchUserData();
      if (userData) {
        dispatch(updateUserData(userData));
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Check if data needs refresh (10 minutes = 600000 ms)
    const REFRESH_INTERVAL = 10 * 60 * 1000;
    const now = new Date();
    const lastRefresh = lastDataRefresh ? new Date(lastDataRefresh) : null;
    
    const shouldRefreshImmediately = !lastRefresh || (now - lastRefresh) >= REFRESH_INTERVAL;

    // Set up interval for periodic refresh
    const startInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      intervalRef.current = setInterval(() => {
        refreshUserData();
      }, REFRESH_INTERVAL);
    };

    // Only refresh immediately if data is stale or missing
    if (shouldRefreshImmediately) {
      console.log("Refreshing user data due to stale data");
      refreshUserData();
    } else {
      console.log("User data is fresh, skipping immediate refresh");
    }

    // Start the interval
    startInterval();

    // Cleanup on unmount or when authentication changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, lastDataRefresh, dispatch]);

  return {
    refreshUserData
  };
};

export default useUserDataRefresh;
